import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { PERMISSIONS } from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { ApprovedRepaymentSchedule } from '../../../components/ApprovedRepaymentSchedule';
import { Button } from '../../../components/Button';
import { FormField } from '../../../components/forms/FormField';
import { PageSpinner } from '../../../components/PageSpinner';
import { StatusBadge } from '../../../components/StatusBadge';
import { applicationsApi } from '../../../features/applications/applications.api';
import { useAuth } from '../../../features/auth/auth-context';
import { customersApi } from '../../../features/customers/customers.api';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatNgn } from '../../../lib/format-currency';
import { approvedDateError, formatBusinessDate, lagosToday } from '../../../lib/format-date';
import { formatStatusLabel } from '../../../lib/format-status';
import { paths, routeTo } from '../../../routes/paths';

import styles from './AdminPortal.module.css';
import { CustomerDocumentsPanel } from './CustomerDocumentsPanel';

function formatTimestamp(value: string | null): string {
  return value === null ? '—' : new Date(value).toLocaleString();
}

function TermsValue({
  label,
  value,
  changed = false,
}: {
  label: string;
  value: React.ReactNode;
  changed?: boolean;
}): React.JSX.Element {
  return (
    <>
      <dt>{label}</dt>
      <dd className={changed ? styles.valueChanged : undefined}>
        {value}
        {changed && <span className={styles.changeTag}>Changed</span>}
      </dd>
    </>
  );
}

export function AdminApplicationDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [firstRepaymentDate, setFirstRepaymentDate] = useState('');
  const [confirmingReject, setConfirmingReject] = useState(false);
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState<string | null>(null);
  const [signatureEvidenceError, setSignatureEvidenceError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (signaturePreviewUrl !== null) URL.revokeObjectURL(signaturePreviewUrl);
    };
  }, [signaturePreviewUrl]);

  const application = useQuery({
    queryKey: ['admin', 'applications', id],
    queryFn: () => applicationsApi.getAdmin(id),
  });

  const customerId = application.data?.customerId ?? '';
  const canReadCustomer = can(PERMISSIONS.CUSTOMERS_READ);
  const customer = useQuery({
    queryKey: ['admin', 'customers', customerId],
    queryFn: () => customersApi.getAdmin(customerId),
    enabled: application.isSuccess && canReadCustomer,
  });
  const addresses = useQuery({
    queryKey: ['admin', 'customers', customerId, 'addresses'],
    queryFn: () => customersApi.listAdminAddresses(customerId),
    enabled: application.isSuccess && canReadCustomer,
  });
  const canReadDocuments = can(PERMISSIONS.CUSTOMER_DOCUMENTS_READ);
  const identityDocuments = useQuery({
    queryKey: ['admin', 'customers', customerId, 'documents'],
    queryFn: () => customersApi.listAdminDocuments(customerId),
    enabled: application.isSuccess && canReadDocuments,
  });

  const needsAdminDate = application.data?.repaymentScheduleModel === 'ADMIN_APPROVED_DATE_V1';
  const firstDateError = approvedDateError(firstRepaymentDate);
  const schedulePreview = useQuery({
    queryKey: [
      'admin',
      'approval-schedule-preview',
      id,
      firstRepaymentDate,
      application.data?.version,
      application.data?.discountAmount,
    ],
    queryFn: () => applicationsApi.previewApprovalSchedule(id, firstRepaymentDate),
    enabled:
      needsAdminDate &&
      application.data?.status === 'UNDER_REVIEW' &&
      firstDateError === undefined &&
      can(PERMISSIONS.SUBSCRIPTIONS_APPROVE),
    retry: false,
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'applications'] });
    await queryClient.invalidateQueries({ queryKey: ['admin', 'applications', id] });
  };

  const review = useMutation({
    mutationFn: () => applicationsApi.startReview(id),
    onSuccess: invalidate,
  });
  const recoverOffer = useMutation({
    mutationFn: () => {
      if (application.data === undefined)
        throw new Error('Refresh the application before recovery.');
      return applicationsApi.recoverExpiredOffer(id, application.data.version);
    },
    onSuccess: async () => {
      setFirstRepaymentDate('');
      setNotes('');
      await invalidate();
    },
  });
  const approve = useMutation({
    mutationFn: () =>
      applicationsApi.approve(
        id,
        notes === '' ? undefined : notes,
        needsAdminDate ? firstRepaymentDate : undefined,
      ),
    onSuccess: invalidate,
  });
  const reject = useMutation({
    mutationFn: () => applicationsApi.reject(id, reason),
    onSuccess: async () => {
      setReason('');
      setConfirmingReject(false);
      await invalidate();
    },
  });
  const requestChanges = useMutation({
    mutationFn: () => applicationsApi.requestChanges(id, reason),
    onSuccess: async () => {
      setReason('');
      await invalidate();
    },
  });
  const setDiscount = useMutation({
    mutationFn: (discountAmount: string) => applicationsApi.setAdminDiscount(id, discountAmount),
    onSuccess: invalidate,
  });

  if (application.isPending) {
    return <PageSpinner label="Loading application" />;
  }

  if (application.isError) {
    return (
      <Alert tone="error">
        {toDisplayMessage(application.error)}{' '}
        <Button variant="secondary" onClick={() => void application.refetch()}>
          Retry
        </Button>
      </Alert>
    );
  }

  const data = application.data;
  const acceptanceSnapshot = data.acceptanceSnapshot ?? null;
  const isLegacyAcceptedContract =
    acceptanceSnapshot === null &&
    [
      'ACCEPTED',
      'AWAITING_MANDATE',
      'MANDATE_ACTIVE',
      'ACTIVE',
      'SUSPENDED',
      'COMPLETED',
      'CANCELLED',
      'DEFAULTED',
    ].includes(data.status);
  const requested = data.requestedTerms ?? null;
  const currentNin = (identityDocuments.data ?? []).find(
    (document) => document.documentType === 'NIN_SLIP' && document.reviewStatus !== 'SUPERSEDED',
  );
  const approvalBlockedByNin =
    canReadDocuments && (identityDocuments.isPending || currentNin?.reviewStatus !== 'VERIFIED');
  const customerLabel = customer.isSuccess
    ? `${customer.data.firstName} ${customer.data.lastName}`
    : `Customer ${data.customerId.slice(0, 8).toUpperCase()}`;
  const mutationError =
    recoverOffer.error ??
    review.error ??
    approve.error ??
    reject.error ??
    requestChanges.error ??
    setDiscount.error;
  const mutationSuccess =
    recoverOffer.isSuccess ||
    review.isSuccess ||
    approve.isSuccess ||
    reject.isSuccess ||
    requestChanges.isSuccess ||
    setDiscount.isSuccess;
  const discountNeedsReview =
    Number(data.discountAmount) > 0 && data.discountProvenance === 'LEGACY_UNVERIFIED';
  const isUnderReview = data.status === 'UNDER_REVIEW';
  const isAwaitingAcceptance = data.status === 'AWAITING_CUSTOMER_ACCEPTANCE';
  const nextStep = (() => {
    if (data.offerExpired === true)
      return {
        title: 'Unaccepted offer expired',
        description:
          'Return it to staff review, choose a new supported date, and issue a revised offer for customer acceptance.',
      };
    if (data.status === 'SUBMITTED') {
      return {
        title: 'Ready for staff review',
        description: 'Start review, then compare the customer’s request with the approval terms.',
      };
    }
    if (isUnderReview && discountNeedsReview) {
      return {
        title: 'Discount authorization needed',
        description:
          'Authorize or remove the legacy discount before this application can be approved.',
      };
    }
    if (isUnderReview) {
      return can(PERMISSIONS.SUBSCRIPTIONS_APPROVE) || can(PERMISSIONS.SUBSCRIPTIONS_UPDATE)
        ? {
            title: 'Review the terms and make a decision',
            description: 'Confirm the requested and final terms, then choose a permitted decision.',
          }
        : {
            title: 'Review in progress',
            description: 'You can inspect this application, but your role cannot make a decision.',
          };
    }
    if (isAwaitingAcceptance) {
      return {
        title: 'Waiting for customer acceptance',
        description: 'Staff review is complete. The customer must accept the approved terms next.',
      };
    }
    if (data.status === 'CHANGES_REQUESTED') {
      return {
        title: 'Waiting for customer changes',
        description:
          'The customer must update and resubmit this application before review continues.',
      };
    }
    if (data.status === 'REJECTED') {
      return {
        title: 'Application closed',
        description: 'This application was rejected and no further staff decision is available.',
      };
    }
    return {
      title: 'No staff action needed',
      description:
        'This application is progressing through its current customer or contract stage.',
    };
  })();

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Application review</p>
          <h1 className={styles.pageTitle}>
            {requested?.productName ?? data.productNameSnapshot ?? 'Solar application'}
          </h1>
          <div className={styles.headerMeta}>
            <span>APP-{data.id.slice(0, 8).toUpperCase()}</span>
            <span>•</span>
            <span>Submitted {formatTimestamp(data.submittedAt)}</span>
            <span>•</span>
            <span>Version {requested?.version ?? data.version}</span>
          </div>
        </div>
      </header>

      <section className={styles.statusGuidance} aria-labelledby="application-next-step-heading">
        <div>
          <p className={styles.eyebrow}>Application status / next step</p>
          <h2 id="application-next-step-heading">{nextStep.title}</h2>
          <p>{nextStep.description}</p>
        </div>
        <StatusBadge status={data.status} />
      </section>

      {mutationError !== null && mutationError !== undefined && (
        <Alert tone="error">{toDisplayMessage(mutationError)}</Alert>
      )}
      {mutationSuccess && <Alert tone="success">Application updated.</Alert>}
      {isAwaitingAcceptance && (
        <Alert tone="success">
          Approval is complete. The application is awaiting customer acceptance.
        </Alert>
      )}

      <div className={styles.reviewGrid}>
        <div className={styles.contentGrid}>
          <section className={styles.card} aria-labelledby="customer-heading">
            <h2 className={styles.cardTitle} id="customer-heading">
              Customer
            </h2>
            <p className={styles.notice}>
              These are the customer’s current live profile and address details, not a historical
              contract-party snapshot.
            </p>
            {customer.isPending && canReadCustomer && <PageSpinner label="Loading customer" />}
            {customer.isError && <Alert tone="error">{toDisplayMessage(customer.error)}</Alert>}
            {customer.isSuccess && (
              <dl className={styles.definitionList}>
                <TermsValue
                  label="Customer"
                  value={`${customer.data.firstName} ${customer.data.lastName}`}
                />
                <TermsValue label="Customer number" value={customer.data.customerNumber} />
                <TermsValue label="Phone" value={customer.data.phoneNumber} />
                <TermsValue label="Profile" value={<StatusBadge status={customer.data.status} />} />
              </dl>
            )}
            {!canReadCustomer && (
              <p className={styles.pageSubtitle}>Your role cannot open customer profile details.</p>
            )}
            <div className={styles.actions}>
              {canReadCustomer && (
                <Link to={routeTo.adminCustomer(data.customerId)}>Open customer record</Link>
              )}
              <Link to={paths.adminApplications}>Back to applications</Link>
            </div>
          </section>

          <CustomerDocumentsPanel
            customerId={data.customerId}
            variant="identity"
            customerLabel={customerLabel}
          />

          <section className={styles.card} aria-labelledby="address-heading">
            <h2 className={styles.cardTitle} id="address-heading">
              Current addresses
            </h2>
            {addresses.isPending && canReadCustomer && <PageSpinner label="Loading addresses" />}
            {addresses.isError && <Alert tone="error">{toDisplayMessage(addresses.error)}</Alert>}
            {addresses.isSuccess && addresses.data.length === 0 && (
              <p className={styles.empty}>
                No current address is recorded. The customer must add an address before address
                details can be reviewed here.
              </p>
            )}
            {addresses.isSuccess && addresses.data.length > 0 && (
              <dl className={styles.definitionList}>
                {addresses.data.map((address) => (
                  <TermsValue
                    key={address.id}
                    label={formatStatusLabel(address.addressType)}
                    value={`${address.addressLine1}, ${address.city}, ${address.stateCode}`}
                  />
                ))}
              </dl>
            )}
          </section>

          <section
            className={`${styles.card} ${styles.wideCard}`}
            aria-labelledby="commercial-heading"
          >
            <h2 className={styles.cardTitle} id="commercial-heading">
              Requested and final terms
            </h2>
            <div className={styles.termsGrid}>
              <article className={styles.termsPanel}>
                <h3>Requested terms</h3>
                <p>Immutable customer request captured when this application was submitted.</p>
                {requested === null ? (
                  <p className={styles.empty}>
                    This legacy submission has no requested-terms snapshot. Historical terms are not
                    reconstructed from today’s catalogue.
                  </p>
                ) : (
                  <dl className={styles.definitionList}>
                    <TermsValue label="Product" value={requested.productName} />
                    <TermsValue label="Plan" value={requested.planName} />
                    <TermsValue label="Price" value={formatNgn(requested.productBasePrice)} />
                    <TermsValue label="Discount" value={formatNgn(requested.discountAmount)} />
                    <TermsValue label="Deposit" value={formatNgn(requested.depositAmount)} />
                    <TermsValue label="Financed" value={formatNgn(requested.financedAmount)} />
                    <TermsValue label="Instalment" value={formatNgn(requested.instalmentAmount)} />
                    <TermsValue label="Tenure" value={`${requested.tenureMonths} months`} />
                    <TermsValue
                      label="Debit day"
                      value={
                        requested.preferredDebitDay === null
                          ? 'Not requested — Admin establishes schedule'
                          : `Day ${requested.preferredDebitDay} (historical customer preference)`
                      }
                    />
                    <TermsValue
                      label="First due date"
                      value={formatBusinessDate(requested.requestedFirstDueDate)}
                    />
                  </dl>
                )}
              </article>

              <article className={styles.termsPanel}>
                <h3>{isAwaitingAcceptance ? 'Approved / final terms' : 'Proposed final terms'}</h3>
                <p>Server-authoritative values used for the staff decision.</p>
                <dl className={styles.definitionList}>
                  <TermsValue
                    label="Product"
                    value={data.productNameSnapshot ?? 'Pending calculation'}
                    changed={
                      requested !== null && requested.productName !== data.productNameSnapshot
                    }
                  />
                  <TermsValue
                    label="Price"
                    value={formatNgn(data.productPrice)}
                    changed={requested !== null && requested.productBasePrice !== data.productPrice}
                  />
                  <TermsValue
                    label="Discount"
                    value={formatNgn(data.discountAmount)}
                    changed={requested !== null && requested.discountAmount !== data.discountAmount}
                  />
                  <TermsValue
                    label="Deposit"
                    value={formatNgn(data.depositAmount)}
                    changed={requested !== null && requested.depositAmount !== data.depositAmount}
                  />
                  <TermsValue
                    label="Financed"
                    value={formatNgn(data.financedAmount)}
                    changed={requested !== null && requested.financedAmount !== data.financedAmount}
                  />
                  <TermsValue
                    label="Instalment"
                    value={formatNgn(data.instalmentAmount)}
                    changed={
                      requested !== null && requested.instalmentAmount !== data.instalmentAmount
                    }
                  />
                  <TermsValue label="Tenure" value={`${data.tenureMonths ?? '—'} months`} />
                  {needsAdminDate && (
                    <TermsValue
                      label="Admin-approved first repayment date"
                      value={formatBusinessDate(data.approvedFirstRepaymentDate)}
                    />
                  )}
                  <TermsValue
                    label="Discount status"
                    value={formatStatusLabel(data.discountProvenance)}
                  />
                </dl>
                {needsAdminDate && data.approvedRepaymentSchedule != null && (
                  <ApprovedRepaymentSchedule rows={data.approvedRepaymentSchedule} />
                )}
              </article>
            </div>
          </section>

          {isLegacyAcceptedContract && (
            <section
              className={`${styles.card} ${styles.wideCard}`}
              aria-labelledby="contract-evidence-heading"
            >
              <h2 className={styles.cardTitle} id="contract-evidence-heading">
                Contract evidence
              </h2>
              <Alert tone="warning">
                Historical acceptance evidence is unavailable for this legacy record. No acceptance
                date, document hash or signature has been reconstructed.
              </Alert>
            </section>
          )}

          {acceptanceSnapshot !== null && (
            <section
              className={`${styles.card} ${styles.wideCard}`}
              aria-labelledby="contract-evidence-heading"
            >
              <h2 className={styles.cardTitle} id="contract-evidence-heading">
                Contract evidence
              </h2>
              <p className={styles.notice}>
                Immutable evidence captured at customer acceptance. It is not reconstructed from
                current profile, catalogue, address or signature data.
              </p>
              <dl className={styles.definitionList}>
                <TermsValue
                  label="Accepted"
                  value={new Date(acceptanceSnapshot.acceptedAt).toLocaleString()}
                />
                <TermsValue label="Contract version" value={acceptanceSnapshot.templateVersion} />
                <TermsValue
                  label="Contract SHA-256"
                  value={
                    <code className={styles.evidenceHash}>{acceptanceSnapshot.contentSha256}</code>
                  }
                />
                <TermsValue
                  label="Signature evidence"
                  value={
                    acceptanceSnapshot.signature === null
                      ? 'Unavailable for this legacy acceptance'
                      : 'Present and bound to this contract'
                  }
                />
                {acceptanceSnapshot.signature !== null && canReadDocuments && (
                  <>
                    <TermsValue
                      label="Signature uploaded"
                      value={new Date(acceptanceSnapshot.signature.uploadedAt).toLocaleString()}
                    />
                    <TermsValue
                      label="Signature type / size"
                      value={`${acceptanceSnapshot.signature.mimeType} · ${(
                        acceptanceSnapshot.signature.byteSize / 1_000_000
                      ).toFixed(2)} MB`}
                    />
                  </>
                )}
              </dl>

              {acceptanceSnapshot.signature !== null && (
                <div className={styles.actions}>
                  {canReadDocuments && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        const signature = acceptanceSnapshot.signature;
                        if (signature === null || signature === undefined) return;
                        void customersApi
                          .previewDocument(signature.documentId, true)
                          .then((blob) => {
                            if (signaturePreviewUrl !== null) {
                              URL.revokeObjectURL(signaturePreviewUrl);
                            }
                            setSignaturePreviewUrl(URL.createObjectURL(blob));
                            setSignatureEvidenceError(null);
                          })
                          .catch((error: unknown) =>
                            setSignatureEvidenceError(toDisplayMessage(error)),
                          );
                      }}
                    >
                      Preview bound signature
                    </Button>
                  )}
                  {can(PERMISSIONS.CUSTOMER_DOCUMENTS_DOWNLOAD) && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        const signature = acceptanceSnapshot.signature;
                        if (signature === null || signature === undefined) return;
                        void customersApi
                          .downloadDocument(signature.documentId, true)
                          .then((blob) => {
                            const url = URL.createObjectURL(blob);
                            const anchor = window.document.createElement('a');
                            anchor.href = url;
                            anchor.download = `contract-signature.${
                              signature.mimeType === 'image/png' ? 'png' : 'jpg'
                            }`;
                            anchor.click();
                            URL.revokeObjectURL(url);
                            setSignatureEvidenceError(null);
                          })
                          .catch((error: unknown) =>
                            setSignatureEvidenceError(toDisplayMessage(error)),
                          );
                      }}
                    >
                      Download bound signature
                    </Button>
                  )}
                </div>
              )}
              {!canReadDocuments && acceptanceSnapshot.signature !== null && (
                <p className={styles.pageSubtitle}>
                  Signature metadata and preview require customer-document read permission.
                </p>
              )}
              {signatureEvidenceError !== null && (
                <Alert tone="error">{signatureEvidenceError}</Alert>
              )}
              {signaturePreviewUrl !== null && (
                <div className={styles.signatureEvidencePreview}>
                  <img src={signaturePreviewUrl} alt="Signature bound to this contract" />
                </div>
              )}
            </section>
          )}
        </div>

        <aside className={styles.reviewSidebar} aria-label="Review decision">
          {(data.offerAmendments ?? [])
            .filter((item) => item.changedFields.includes('expiredUnacceptedOffer'))
            .map((item) => (
              <section className={styles.card} key={item.id}>
                <h2 className={styles.cardTitle}>Expired unaccepted offer history</h2>
                <p>{item.reason}</p>
                <p>
                  Returned to review {formatTimestamp(item.createdAt)}. This is not an accepted
                  contract.
                </p>
                <details>
                  <summary>Previous frozen offer and schedule</summary>
                  <pre className={styles.offerHistory}>
                    {JSON.stringify(item.requestedValues, null, 2)}
                  </pre>
                </details>
              </section>
            ))}
          {data.offerExpired === true && (
            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Return expired offer to review</h2>
              <p>
                No automatic date shift or renewal. The previous offer is retained in history; Admin
                must approve a new 1–28 date and the customer must review and accept again.
              </p>
              {can(PERMISSIONS.SUBSCRIPTIONS_UPDATE) && (
                <Button busy={recoverOffer.isPending} onClick={() => recoverOffer.mutate()}>
                  Return expired offer to staff review
                </Button>
              )}
            </section>
          )}
          {discountNeedsReview && (
            <section
              className={`${styles.card} ${styles.warningCard}`}
              aria-labelledby="discount-review-heading"
            >
              <h2 className={styles.cardTitle} id="discount-review-heading">
                Legacy discount requires authorization
              </h2>
              <Alert tone="warning">
                The {formatNgn(data.discountAmount)} discount must be authorized or removed before
                approval.
              </Alert>
              {can(PERMISSIONS.SUBSCRIPTIONS_UPDATE) ? (
                <div className={styles.actions}>
                  <Button
                    busy={setDiscount.isPending}
                    onClick={() => setDiscount.mutate(data.discountAmount)}
                  >
                    Authorize existing discount
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={setDiscount.isPending}
                    onClick={() => setDiscount.mutate('0.00')}
                  >
                    Remove discount
                  </Button>
                </div>
              ) : (
                <p className={styles.pageSubtitle}>
                  A staff member with application update permission must resolve this discount.
                </p>
              )}
            </section>
          )}

          <section className={styles.card} aria-labelledby="review-actions-heading">
            <h2 className={styles.cardTitle} id="review-actions-heading">
              Review decision
            </h2>

            {data.status === 'SUBMITTED' && (
              <>
                <p className={styles.pageSubtitle}>
                  Start review before requesting changes, rejecting, or approving this application.
                </p>
                <div className={styles.actions}>
                  <Button busy={review.isPending} onClick={() => review.mutate()}>
                    Start review
                  </Button>
                </div>
              </>
            )}

            {isUnderReview && can(PERMISSIONS.SUBSCRIPTIONS_APPROVE) && (
              <>
                {approvalBlockedByNin && (
                  <Alert tone="warning">
                    Final approval is blocked until the customer&apos;s current NIN slip is
                    verified.
                  </Alert>
                )}
                {needsAdminDate && (
                  <>
                    <FormField
                      label="First repayment date"
                      name="firstRepaymentDate"
                      type="date"
                      min={lagosToday()}
                      required
                      value={firstRepaymentDate}
                      onChange={(event) => setFirstRepaymentDate(event.target.value)}
                      hint="Admin-approved calendar day must be 1–28. Review the derived schedule before approval."
                      error={firstRepaymentDate === '' ? undefined : firstDateError}
                    />
                    {firstRepaymentDate === '' && (
                      <p className={styles.notice}>
                        Choose the first repayment date to enable approval.
                      </p>
                    )}
                    {schedulePreview.isFetching && (
                      <PageSpinner label="Calculating repayment schedule" />
                    )}
                    {schedulePreview.isError && (
                      <Alert tone="error">{toDisplayMessage(schedulePreview.error)}</Alert>
                    )}
                    {firstDateError === undefined && schedulePreview.isSuccess && (
                      <ApprovedRepaymentSchedule
                        rows={schedulePreview.data}
                        title="Approval schedule preview"
                      />
                    )}
                  </>
                )}
                <FormField
                  label="Approval notes"
                  name="notes"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
                <div className={styles.actions}>
                  <Button
                    busy={approve.isPending}
                    disabled={
                      discountNeedsReview ||
                      approvalBlockedByNin ||
                      (needsAdminDate &&
                        (firstDateError !== undefined ||
                          !schedulePreview.isSuccess ||
                          schedulePreview.isFetching))
                    }
                    onClick={() => approve.mutate()}
                  >
                    Approve application
                  </Button>
                </div>
              </>
            )}

            {isUnderReview && can(PERMISSIONS.SUBSCRIPTIONS_UPDATE) && (
              <>
                <FormField
                  label="Decision reason"
                  name="reason"
                  value={reason}
                  required
                  onChange={(event) => setReason(event.target.value)}
                />
                <div className={styles.actions}>
                  <Button
                    variant="secondary"
                    busy={requestChanges.isPending}
                    disabled={reason.trim() === '' || confirmingReject}
                    onClick={() => requestChanges.mutate()}
                  >
                    Request changes
                  </Button>
                  <Button
                    variant="danger"
                    disabled={reason.trim() === '' || requestChanges.isPending}
                    onClick={() => setConfirmingReject(true)}
                  >
                    Reject application
                  </Button>
                </div>
                {confirmingReject && (
                  <div className={styles.confirmPanel} role="alert">
                    <p>
                      Rejecting ends this application journey. Confirm only after reviewing the
                      customer’s request and the reason above.
                    </p>
                    <div className={styles.actions}>
                      <Button
                        variant="danger"
                        busy={reject.isPending}
                        onClick={() => reject.mutate()}
                      >
                        Confirm rejection
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={reject.isPending}
                        onClick={() => setConfirmingReject(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}

            {data.status !== 'SUBMITTED' && !isUnderReview && !isAwaitingAcceptance && (
              <p className={styles.pageSubtitle}>
                No staff decision is available while this application is{' '}
                {formatStatusLabel(data.status).toLowerCase()}.
              </p>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}
